import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, FormControl,
  FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon,
  VerifiedUser as ValidateIcon,
} from '@mui/icons-material';
import { agentsAPI } from '../services/api';
import TrustDetails from './TrustDetails';

/**
 * Cross-domain trusts for ONE domain controller.
 *
 * A trust is not an object inside this directory; it is a security boundary
 * between two of them. Samba's own documentation calls this support
 * EXPERIMENTAL and states that no SID filtering is applied to a forest trust,
 * which means the trusted domain's controllers can grant Domain Admin here.
 *
 * That warning is repeated at the point of DECISION rather than only in a doc,
 * because a warning nobody reads while clicking is not a warning. The create
 * dialog also requires the domain name to be typed back: the cost of getting
 * this wrong is another organisation holding authority in your directory, and
 * that deserves more friction than a single click.
 */
function TrustsPanel({ agentName, isAdmin, onError }) {
  const [trusts, setTrusts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmName, setConfirmName] = useState('');

  const [form, setForm] = useState({
    domain: '', trust_type: 'external', direction: 'incoming',
    remote_username: 'Administrator', remote_password: '', quarantined: true,
  });

  const load = useCallback(async () => {
    if (!agentName) return;
    setLoading(true);
    try {
      const r = await agentsAPI.listTrusts(agentName);
      setTrusts(r.data?.trusts || []);
    } catch (e) {
      onError?.(e.response?.data?.detail || 'Could not list trusts');
    } finally {
      setLoading(false);
    }
  }, [agentName, onError]);

  useEffect(() => {
    // Reload when the selected DC changes: trusts belong to a domain, and
    // showing one controller's trusts under another's name would be a lie.
    setTrusts([]);
    setChecked({});
    load();
  }, [load]);

  const validate = async (domain) => {
    setBusy(true);
    try {
      const r = await agentsAPI.validateTrust(agentName, domain);
      setChecked((p) => ({ ...p, [domain]: r.data }));
    } catch (e) {
      onError?.(e.response?.data?.detail || `Could not validate ${domain}`);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await agentsAPI.createTrust(agentName, {
        ...form,
        quarantined: form.quarantined ? 'yes' : 'no',
      });
      setCreateOpen(false);
      setConfirmName('');
      setForm((f) => ({ ...f, domain: '', remote_password: '' }));
      await load();
    } catch (e) {
      onError?.(e.response?.data?.detail || 'Could not create the trust');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await agentsAPI.deleteTrust(agentName, { domain: deleteTarget });
      setDeleteTarget(null);
      await load();
    } catch (e) {
      onError?.(e.response?.data?.detail || 'Could not delete the trust');
    } finally {
      setBusy(false);
    }
  };

  if (!agentName) {
    return <Alert severity="info">Select a domain controller to see its trusts.</Alert>;
  }

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Samba's trust support is experimental</AlertTitle>
        Per Samba's own documentation: a <strong>forest</strong> trust applies{' '}
        <strong>no SID filtering</strong>, so the trusted domain's controllers can grant Domain
        Admin in this one. Both sides must fully trust each other. Users and groups from a
        trusted domain <strong>cannot</strong> be added to this domain's groups.
      </Alert>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Trusts held by <strong>{agentName}</strong>
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
        {isAdmin && (
          <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}>
            Create a trust
          </Button>
        )}
      </Stack>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack>
      ) : trusts.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No trusts. This domain stands alone.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {trusts.map((t) => {
            // samba-tool prints one trust per line as
            //   Type[External] Transitive[No]  Direction[BOTH]  Name[testlab.internal]
            // Taking the first whitespace-delimited token gave "Type[External]"
            // as the trust's name -- which is what was shown in the list, and
            // what the details lookup was keyed on, so every trust reported
            // "not recorded" while a junk row accumulated under that label.
            const line = String(t);
            const field = (k) => (line.match(new RegExp(`${k}\\[([^\\]]*)\\]`)) || [])[1];
            const name = field('Name') || line.split(/\s+/)[0];
            const tType = field('Type');
            const tDirection = field('Direction');
            const tTransitive = field('Transitive');
            const v = checked[name];
            return (
              <Paper key={name} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}
                       sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                    <strong>{name}</strong>
                  </Typography>
                  {tType && <Chip size="small" variant="outlined" label={tType} />}
                  {tDirection && (
                    <Chip size="small" variant="outlined"
                          label={tDirection === 'BOTH' ? 'two-way' : tDirection.toLowerCase()} />
                  )}
                  {tTransitive === 'No' && (
                    <Tooltip title="Trust is not transitive: it does not extend to domains trusted by that domain.">
                      <Chip size="small" variant="outlined" label="not transitive" />
                    </Tooltip>
                  )}
                  {v && (
                    <Tooltip title={v.detail || ''}>
                      <Chip size="small" color={v.valid ? 'success' : 'error'}
                            label={v.valid ? 'validates' : 'NOT validating'} />
                    </Tooltip>
                  )}
                  <Button size="small" startIcon={<ValidateIcon />} disabled={busy}
                          onClick={() => validate(name)}>
                    Validate
                  </Button>
                  {isAdmin && (
                    <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={busy}
                            onClick={() => setDeleteTarget(name)}>
                      Delete
                    </Button>
                  )}
                </Stack>
                {/* Who established this, when, on which addresses, and the
                    last validation -- none of which the DC can tell us. */}
                <TrustDetails agentName={agentName} domain={name} isAdmin={isAdmin} />
                {t !== name && (
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                    {t}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Creating a trust. The friction here is deliberate. */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create a trust from {agentName}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>This grants another directory authority in yours</AlertTitle>
            A <strong>forest</strong> trust applies no SID filtering in Samba: the other domain's
            controllers can assert Domain Admin here. Prefer <strong>external</strong> with
            quarantine on, and prefer <strong>incoming</strong> over <strong>both</strong>,
            unless you specifically need more.
          </Alert>

          <TextField label="Other domain (FQDN)" fullWidth sx={{ mb: 2 }}
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            placeholder="ad.contoso.com" />

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={form.trust_type}
                onChange={(e) => setForm({
                  ...form,
                  trust_type: e.target.value,
                  // Mirror samba-tool's own defaults so the switch reflects
                  // what would actually happen rather than a stale choice.
                  quarantined: e.target.value === 'external',
                })}>
                <MenuItem value="external">external (recommended)</MenuItem>
                <MenuItem value="forest">forest</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select label="Direction" value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                <MenuItem value="incoming">incoming</MenuItem>
                <MenuItem value="outgoing">outgoing</MenuItem>
                <MenuItem value="both">both</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <FormControlLabel sx={{ mb: 2 }}
            control={<Switch checked={form.quarantined}
              onChange={(e) => setForm({ ...form, quarantined: e.target.checked })} />}
            label="Quarantined (apply SID filtering)" />
          {!form.quarantined && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              With quarantine off, SIDs from the other domain are accepted as presented.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            An administrator of the OTHER domain. The password is sent to the agent, used once,
            and never written to the audit row.
          </Typography>
          <TextField label="Remote username" fullWidth sx={{ mb: 2 }}
            value={form.remote_username}
            onChange={(e) => setForm({ ...form, remote_username: e.target.value })} />
          <TextField label="Remote password" type="password" fullWidth sx={{ mb: 2 }}
            value={form.remote_password}
            onChange={(e) => setForm({ ...form, remote_password: e.target.value })} />

          <TextField
            label={`Type "${form.domain || 'the domain'}" to confirm`}
            fullWidth value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setConfirmName(''); }}>Cancel</Button>
          <Button color="error" variant="contained"
            disabled={busy || !form.domain || !form.remote_password
                      || confirmName !== form.domain}
            onClick={create}>
            Create the trust
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete the trust with “{deleteTarget}”?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Authentication that depends on this trust stops working immediately. The other side's
            half of the trust is not removed by this — it has to be deleted there too, or it is
            left pointing at nothing.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={remove}>
            Delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrustsPanel;
